import React, { useMemo, useState } from "react";

import { Accordion, AccordionDetails, AccordionSummary, Stack } from "@mui/material";
import { CurrencyType, MeasureParameter } from "store/types";

import { GroupHead } from "./GroupHead";
import { Group as IGroup, MeasureDataByRound, useGroupsForm } from "./GroupsFormContext";
import { Section } from "./Section";

interface Props extends IGroup {
  measureParameters: MeasureParameter[];
  subDeals: { title: string; id: number }[];
  parentId: number | null;
  defaultCurrency: CurrencyType;

  enableDealSelect: boolean;

  disableDealSelect?: boolean;
  disableSubDealSelect?: boolean;
  disableRemoveGroup?: boolean;
  disableAddMeasureData?: boolean;
}

export const Group: React.FC<Props> = (props) => {
  const {
    measure_id,
    id,
    deal_id,
    measureParameters,
    defaultCurrency,
    disableAddMeasureData,
    sub_deal_id,
    parentId,
    files,
    total_amount
  } = props;

  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const { selectFromMeasureData, getDealRoundsFromState } = useGroupsForm();

  const measuresData = selectFromMeasureData({ group_id: id });
  const selectedMeasure = measureParameters.find((measure) => measure.id === measure_id) || null;

  const isGeneral = selectedMeasure?.is_global_value;
  const isCurrencyMeasure = selectedMeasure?.measure_group.is_currency || false;

  // Effective parent id is either given parentId, or if the user selected a sub deal - the deal itself is the parent.
  // const effectiveParentId = parentDealId || (sub_deal_id ? props.deal_id : null);

  const roundsWithInvestors: MeasureDataByRound[] = useMemo(() => {
    const result: MeasureDataByRound[] = [];
    const dealIdToPickRounds = sub_deal_id || deal_id;
    const rounds = getDealRoundsFromState({ dealId: dealIdToPickRounds, filterDeletedRounds: true });

    if (!rounds) return result;

    rounds.forEach((round) => {
      result.push({
        round_id: round.id,
        title: round.name,
        measureData: measuresData.filter((item) => item.round_id === round.id && item.group_id === id)
      });
    });

    return result;
  }, [deal_id, sub_deal_id, measuresData, getDealRoundsFromState]);

  const isSingleRound: boolean = useMemo(() => roundsWithInvestors.length === 1, [roundsWithInvestors.length]);

  const isAllowedNegative = useMemo(() => !(selectedMeasure?.is_absolute || false), [selectedMeasure]);

  return (
    <Stack
      className="group"
      sx={{
        gap: "20px",
        p: "0 20px"
      }}
    >
      <Accordion
        disableGutters
        expanded={isExpanded}
        sx={{
          "&.MuiAccordion-root:before": {
            backgroundColor: "transparent"
          },
          bgcolor: "transparent",
          border: "none",
          boxShadow: "none"
        }}
      >
        <AccordionSummary
          sx={{
            pl: 0,
            m: 0,
            ".MuiAccordionSummary-content": {
              m: 0
            }
          }}
          aria-controls="panel1a-content"
          id="panel1a-header"
        >
          <GroupHead
            enableExpandArrow={Boolean(measuresData.length)}
            onIconClick={() => setIsExpanded((prevState) => !prevState)}
            selectedMeasure={selectedMeasure}
            isExpanded={isExpanded}
            hideRoundsManage={isGeneral || !measuresData.length}
            {...props}
          />
        </AccordionSummary>
        {deal_id && (
          <AccordionDetails sx={{ p: 0 }}>
            <Stack>
              {isGeneral && measure_id && measuresData.length === 1 && (
                <Section
                  isMeasureGlobalValue={true}
                  defaultCurrency={defaultCurrency}
                  isCurrencyMeasure={isCurrencyMeasure}
                  hideRoundName
                  measureData={measuresData}
                  deal_id={deal_id}
                  group_id={id}
                  isAllowedNegative={isAllowedNegative}
                  disableAddMeasureData={disableAddMeasureData}
                  parentDealId={parentId}
                  displaySecondPart={false}
                  groupFiles={files}
                  total_amount={total_amount}
                />
              )}
              {!isGeneral &&
                measure_id &&
                roundsWithInvestors.map((round) => (
                  <Section
                    isMeasureGlobalValue={false}
                    defaultCurrency={defaultCurrency}
                    isCurrencyMeasure={isCurrencyMeasure}
                    key={round.round_id}
                    {...round}
                    hideRoundName={isSingleRound}
                    deal_id={deal_id}
                    group_id={id}
                    isAllowedNegative={isAllowedNegative}
                    disableAddMeasureData={disableAddMeasureData}
                    parentDealId={sub_deal_id ? deal_id : parentId}
                    groupFiles={files}
                    total_amount={total_amount}
                  />
                ))}
            </Stack>
          </AccordionDetails>
        )}
      </Accordion>
    </Stack>
  );
};
